export type VoucherMatchingProcess = "markup" | "matched";

export type VoucherMatchingRunState =
  | "NOT_RUN"
  | "PROCESSING"
  | "SUCCESS"
  | "FAILED"
  | "MISSING_FILE";

export interface VoucherMatchingRunStatus {
  reportDate: string;
  process: VoucherMatchingProcess;
  status: VoucherMatchingRunState;
  hasSuccessfulRun: boolean;
  lastAttemptAt: string | null;
  lastSuccessfulAt: string | null;
  fileName: string | null;
}

export interface VoucherMatchingRunsResult {
  range: { start: string; end: string };
  runs: VoucherMatchingRunStatus[];
}

export type VoucherRunStatusIndex = Record<
  string,
  Record<VoucherMatchingProcess, VoucherMatchingRunStatus>
>;

type VoucherRunAvailability = "loading" | "available" | "unavailable";

export interface VoucherRunActionState {
  disabled: boolean;
  confirmRerun: boolean;
  label: string;
}

export const getVoucherRunActionState = ({
  availability,
  run,
}: {
  availability: VoucherRunAvailability;
  run?: VoucherMatchingRunStatus;
}): VoucherRunActionState => {
  if (availability === "loading") {
    return { disabled: true, confirmRerun: false, label: "Loading" };
  }
  if (availability === "unavailable" || !run) {
    return { disabled: true, confirmRerun: false, label: "Unavailable" };
  }

  const labels: Record<VoucherMatchingRunState, string> = {
    NOT_RUN: "Run",
    PROCESSING: "Processing",
    SUCCESS: "Completed",
    FAILED: "Retry",
    MISSING_FILE: "Missing file",
  };
  return {
    disabled: run.status === "PROCESSING",
    confirmRerun: run.hasSuccessfulRun,
    label: labels[run.status],
  };
};

export const indexRunStatuses = (
  result: VoucherMatchingRunsResult,
): VoucherRunStatusIndex => {
  const expected = expectedKeys(result.range.start, result.range.end);
  const indexed: VoucherRunStatusIndex = {};
  const seen = new Set<string>();

  for (const run of result.runs) {
    const key = `${run.reportDate}:${run.process}`;
    if (!expected.has(key) || seen.has(key)) {
      throw new Error("Voucher run status response is incomplete");
    }
    seen.add(key);
    indexed[run.reportDate] ??= {} as Record<
      VoucherMatchingProcess,
      VoucherMatchingRunStatus
    >;
    indexed[run.reportDate][run.process] = run;
  }

  if (seen.size !== expected.size) {
    throw new Error("Voucher run status response is incomplete");
  }
  return indexed;
};

const expectedKeys = (start: string, end: string): Set<string> => {
  const first = parseDate(start);
  const last = parseDate(end);
  if (!first || !last || first > last) {
    throw new Error("Voucher run status response is incomplete");
  }

  const keys = new Set<string>();
  for (const date = new Date(first); date <= last; ) {
    const reportDate = date.toISOString().slice(0, 10);
    keys.add(`${reportDate}:markup`);
    keys.add(`${reportDate}:matched`);
    date.setUTCDate(date.getUTCDate() + 1);
  }
  return keys;
};

const parseDate = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value
    ? null
    : date;
};
